// ============================================
// PRTCL (formerly PenRx) App Entry
//
// Routes between two completely separate UI trees:
//   - <DesktopApp/>  ≥1024px web (lazy-loaded, iOS bundle excludes it)
//   - <Router/>      everything else (mobile web, PWA, iOS native)
//
// The decision is made once at mount and locked. We do not swap trees on
// window resize — that would destroy in-flight state. Refresh re-evaluates.
// ============================================

import { lazy, Suspense, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { Router } from './app/Router';
import { SplashScreen } from './components/SplashScreen';
import { isNative } from './utils/native';
import './components/SplashScreen.css';

const DesktopApp = lazy(() => import('./features/desktop/DesktopApp'));

// Web-only and ≥1024px = desktop tree. Capacitor (iOS native) always
// returns false here, so the desktop chunk never loads on device.
function shouldUseDesktop(): boolean {
  if (typeof window === 'undefined') return false;
  if (isNative()) return false;
  return window.innerWidth >= 1024;
}

function App() {
  const [useDesktop] = useState(shouldUseDesktop);

  if (useDesktop) {
    return (
      <HelmetProvider>
        <Suspense fallback={null}>
          <DesktopApp />
        </Suspense>
        <Analytics />
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <Router />
      {/* The boot splash is the iOS app's (it hands off from the native
          LaunchScreen, see main.tsx). On mobile web it sat over the
          prerendered landing page for ~2s and blocked scrolling. */}
      {isNative() && <SplashScreen />}
      <Analytics />
    </HelmetProvider>
  );
}

export default App;
